import { DateTime } from 'luxon';

import { UserSubscription } from '@appTypes/esb';

/**
 * Resolve the single subscription record that currently applies for a topic.
 *
 * The API can return several rows for the same topicId (duplicate rows are
 * appended instead of updated), so both the checkbox state and the save diff
 * must agree on which one is authoritative. We keep the rows that are in
 * effect today (effectiveDt reached, not expired, not deleted) and, on ties,
 * take the highest id as the most recent one.
 */
export default function getActiveSubscription(
  subs: UserSubscription[],
  topicId: number | string,
): UserSubscription | undefined {
  const today = DateTime.now().startOf('day');

  return subs
    .filter(sub => Number(sub.topicId) === Number(topicId) && !sub.isDelete)
    .filter(sub => {
      const effective = sub.effectiveDt
        ? DateTime.fromISO(sub.effectiveDt).startOf('day')
        : undefined;
      const expiry = sub.expiryDt
        ? DateTime.fromISO(sub.expiryDt).startOf('day')
        : undefined;

      const hasStarted = !effective || effective <= today;
      const notExpired = !expiry || expiry >= today;

      return hasStarted && notExpired;
    })
    .reduce<UserSubscription | undefined>(
      (active, sub) =>
        !active || Number(sub.id) > Number(active.id) ? sub : active,
      undefined,
    );
}
